<template lang="pug">
.screen-hotspot-game.dont-unlock.col-span-edge.relative(ref="elRef")
  .hsg-section.intro(
    :data-audio="getScreenAudio(`${screen.id}_intro`)"
    :data-intro-index="i"
    :id="screen.id + '_intro'"
    v-for="(s, i) in screen.intro"
  )
    audio-marker(:name="`${screen.id}_intro`")
    uc-screen(
      :editPath="editPath + '.intro.' + i"
      :nested="true"
      :screen="{ ...s, center: true, fullscreen: true }"
    )
      .text-center.self-center
        uc-button.btn.btn-primary.ripple(
          @click="scrollToId(`${screen.id}_level_0`)"
        )
          uc-text(text="Begin")

  div.h-auto(v-show="suspend?.completed[`${screen.id}_begin`] || wordDoc || audioDoc")
    .hg-wrapper-score.dark.sticky.inset-0.-mb-32.py-10.z-20.flex.justify-end.pointer-events-none(
      v-if="isScored"
    )
      span.flex.items-center.gap-3.backdrop-blur-sm.bg-gray-700.bg-opacity-70.corners.mx-20.w-96.max-w-full.px-4.py-2
        uc-icon.text-amber-400.text-3xl(icon="mdi:star")
        .bg-gray-900.h-4.flex-1.overflow-hidden.relative.bg-opacity-50
          .bg-amber-400.fill.transition.duration-1000(
            :style="{ transformOrigin: '0 0', transform: `scaleX(${scorePercent / 100})` }"
          )

    template(
      v-for="({ hotspot, targets, id, style, lightboxstyles, hideHotspotBg, ...level }, i) in screen.levels"
    )
      .hsg-section(
        :data-audio="getScreenAudio(`${screen.id}-level-${i}`)"
        :id="`${screen.id}_level_${i}`"
        :class="`level_${i}`"
      )
        .overflow-hidden.z-10.lg_h-screen(
          :class="[style, 'bg-primary-900/90']"
          v-if="unlockedLevel(i) || unlockScreens || wordDoc || audioDoc"
        )
          .hg-flex-wrapper.relative.z-0
            .lg_h-screen.bg-gray-700.bg-gradient-to-bl.from-transparent.to-gray-900.bg-fixed.py-8
              .hg-wrapper-hotspot.fill.z-0.overflow-hidden.lg_h-screen
                screen-hotspot.w-full.lg_h-screen(
                  :editPath="editPath + '.levels.' + i"
                  :screen="{ hotspot, targets, id, lightboxstyles, hideHotspotBg, 'hotspot-game': true }"
                  @allCompleted="onAllCompleted(i)"
                )
              .z-10.dark
                .my-20.mx-4.lg_mx-20.col-span-edges.lg_w-96.max-w-full(
                  uc-parallax="2"
                )
                  audio-marker(:name="`${screen.id}-level-${i}`")
                  .backdrop-blur-sm.bg-gray-900.bg-opacity-70.corners.p-8
                    p.mb-6.text-gray-400(v-if="screen.levels.length > 1")
                      uc-text.tracking-widest.text-sm.uppercase(
                        :values="{ n: i + 1, m: screen.levels.length }"
                        text="Stage ${n} of ${m}"
                      )
                    uc-screen(
                      :editPath="`${editPath}.levels.${i}`"
                      :nested="true"
                      :screen="{ id, 'no-wrap': true, 'no-fade': true, ...level }"
                    )

                    //- add a lightbox teleport target after each HSG level to include the content at the correct place in the docs
                    template(v-for="name in Object.keys(targets)" v-if="wordDoc || audioDoc")
                      .word-audio-hotspotgame-lightbox-target(
                        :data-teleport="`lightbox_${id}_${name}`"
                      )

          //- the cards for small devices
          screen-cards.p-10.lg_hidden.screen--no-fade(
            :editPath="`${editPath}.levels.${i}`"
            :nested="true"
            :screen="{ cols: 3, cards: getCardsFromTargets(screen.levels[i]) }"
            uc-word-skip=""
            uc-dont-read=""
          )
            uc-screen(
              :editPath="`${editPath}.levels.${i}`"
              :nested="true"
              :screen="s"
            )

  //- we include the results screen for audio/word only if this HSG is NOT scored (no questions means no dynamic ${scorePercent} to display or read out)
  .hsg-section.result(
    :data-audio="getScreenAudio(`${screen.id}_result`)"
    :id="`${screen.id}_result`"
    v-for="(s, i) in activeResult"
    v-if="(isScored && !(audioDoc || wordDoc)) || !isScored"
    v-show="unlockedResult || audioDoc || wordDoc"
    :class="{ 'dont-unlock': isRetry && !isPassed }"
  )
    audio-marker(:name="`${screen.id}_result`")
    uc-screen(
      :key="s.id || s.scoreThreshold ? `${screen.id}_result-${s.scoreThreshold}-${s.index}` : `${screen.id}_result-${i}`"
      :editPath="s.scoreThreshold ? `${editPath}.result.${s.scoreThreshold}.${s.index}` : `${editPath}.result.${i}`"
      :nested="true"
      :screen="{ ...s, center: true, fullscreen: true }"
    )
      transition-fade(:delay="1")
        .text-right.w-full.my-8(v-if="isRetry && !isPassed")
          uc-button.btn.btn-primary.ripple.w-full.sm_w-auto(
            @click="reset" 
            data-sfx="continue"
          )
            uc-text(
              :k="retryText"
              :text="retryText"
            )

</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  toRef,
  unref,
  watchEffect
} from 'vue';
import gsap from 'gsap';
import useSuspend from '../lib/useSuspend';
import { setCompleted, set } from '../lib/SCORM';
import ScrollTrigger from 'gsap/ScrollTrigger';
import useAudio from '../stores/useAudio';
import { play, stop } from '../lib/audio';
import hotspotGameSchema from '../lib/screen-schemas/screen-hotspot-game.schema';
import useScreenAudio, { type NarratableScreen } from '../lib/useScreenAudio';
import useAudioNarration from '../lib/useAudioNarration';
import {
  buildHsgSectionRowText,
  hsgIntroMarkerKey,
  hsgLevelMarkerKey,
  hsgResultMarkerKey,
  buildHsgResultText
} from '../lib/hotspotGameAudio';

const suspend = useSuspend();

const elRef = ref<HTMLElement | null>(null);
const passmark = ref<number>(50);
const retryText = ref<string>('Try again');

interface Props extends UcScreenRequiredId {
  intro: UcScreen[];
  result: UcScreen[];
  levels: ScreenHotspot[];
  retry?: string | boolean;
  passmark?: number;
}

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));

const props = defineProps<{ screen: Props; editPath: string }>();

// provide('sectionId', props.screen.id + '__dontunlock');

const scoredTemplates = ['question'];

// gather targets that are scored
const scoredIds = computed(() => {
  const ids: string[] = [];

  props.screen.levels.forEach(level => {
    Object.values(level.targets).forEach(([screen]) => {
      const { template, id } = screen;
      if (template && scoredTemplates.includes(template)) ids.push(id);
    });
  });

  return ids;
});

const isScored = computed(() => {
  return scoredIds.value.length > 0;
});

const score = computed(() => {
  let score = 0;
  for (const id of scoredIds.value) if (suspend.correctQuestions[id]) score++;

  return score;
});

const scorePercent = computed(() =>
  Math.round((score.value / scoredIds.value.length) * 100)
);

const isRetry = computed(() => {
  if (!isScored.value) return false;

  let retry = false;

  // order the score thresholds from highest to lowest
  const keys = Object.keys(props.screen.result)
    .map(k => +k)
    .sort((a, b) => b - a)
    .map(num => String(num));

  for (const k of keys) {
    if (props.screen.result[k][0].retry) {
      passmark.value = +k;
      if (typeof props.screen.result[k][0].retry === 'string')
        retryText.value = props.screen.result[k][0].retry;
      return true;
    }
  }
  return retry;
});

const isPassed = computed(() => {
  if (!passmark.value) return false;
  return scorePercent.value >= passmark.value;
});

const scrollToId = async (id: string) => {
  const el = unref(elRef);
  if (!el) return;

  // when you click the start button, we have to unlock the first level
  if (id === `${props.screen.id}_level_0`) {
    setCompleted(`${props.screen.id}_begin`);

    await nextTick();

    setupScrollTriggers('level_0');
  } else {
    // pause before scrolling down after completing each level
    await new Promise(r => setTimeout(r, 500));
  }

  const scroller = el.closest('.scroller');

  return new Promise(resolve => {
    gsap.to(scroller, { scrollTo: '#' + id, onComplete: resolve });
  });
};

const { getAudioURL } = useAudio();
const { resolveSlot } = useScreenAudio(
  toRef(props, 'screen'),
  hotspotGameSchema
);
const { resolveText, audioAiJitEnabled } = useAudioNarration();

const getScreenAudio = screenID => {
  return getAudioURL(screenID);
};

const getLightboxId = (id: string, name: string) => `lightbox_${id}_${name}`;

/** Invalidate in-flight JiT resolve when another HSG section starts playing. */
let sectionPlayGeneration = 0;

const getCardsFromTargets = level => {
  let cards = [];
  // create a card for each target to open the question in a light box
  // match the lightbox ID format from hotspot for completion (lightbox_${id}_${name}) id is the level id and name is the target object name
  Object.values(level.targets)
    .flat()
    .forEach((target, i) => {
      const card = {
        id: target.id ?? `${level.id}-target${i}`,
        heading: target?.heading?.trim(),
        subheading: target?.subheading?.trim(),
        opens: getLightboxId(level.id, Object.keys(level.targets)[i]),
        ...target.card
      };
      cards.push(card);
    });

  return cards;
};

const unlockedLevel = (i: number) =>
  i === 0
    ? suspend.completed[`${props.screen.id}_begin`]
    : suspend.completed[props.screen.levels[i - 1].id];

const unlockedResult = computed(
  () => suspend.completed[props.screen.levels.slice(-1)[0].id]
);

const onAllCompleted = async (i: number) => {
  const levelCount = props.screen.levels.length;
  const el = unref(elRef);

  if (i < levelCount - 1) {
    // we need to wait for the screen unlock and render with some height before creating the scroll triggers
    await nextTick();

    setupScrollTriggers(`level_${i + 1}`);

    gsap.delayedCall(1, () => {
      scrollToId(`${props.screen.id}_level_${i + 1}`);
    });
  } else if (i === levelCount - 1) {
    const hsgScreen = el?.closest('.section--active .screen--hotspot-game');

    if (hsgScreen && props.screen?.result) {
      gsap.delayedCall(1, () => {
        scrollToId(`${props.screen.id}_result`);
      });
    }

    if (!isRetry.value || isPassed.value) setCompleted(props.screen.id);

    await nextTick();

    setupScrollTriggers('result');

    nextTick(() => {
      const page = elRef.value?.closest('.page') as UcPageElement;
      if (!isRetry.value || isPassed.value) page.refreshSections();
    });
  }
};

watchEffect(async () => {
  if (unlockedResult.value) {
    if (isRetry.value && !isPassed.value) return;
    setCompleted(props.screen.id);
    await nextTick();
    ScrollTrigger.refresh();
  }
});

const activeResult = computed(() => {
  // if there are no scores, return the first screen
  if (!isScored.value) {
    return props.screen.result && props.screen.result[0]
      ? props.screen.result[0]
      : [];
  }

  // order the score thresholds from highest to lowest
  const keys = Object.keys(props.screen.result)
    .map(k => +k)
    .sort((a, b) => b - a)
    .map(num => String(num));

  // search for the minimum key surpassed by the user's score out of 100
  for (const k of keys) {
    if (scorePercent.value >= +k) {
      const screens = props.screen.result[k];
      return screens.map((screen, i) => ({
        ...screen,
        scoreThreshold: k,
        index: i
      }));
    }
  }

  // if no key is found, return the last key (lowest score threshold)
  // Get the lowest score threshold key (i.e., the last element in the keys array, which is sorted descending)
  const lastKey = keys.slice(-1)[0];
  const screens = props.screen.result[lastKey];
  return screens.map((screen, i) => ({
    ...screen,
    scoreThreshold: lastKey,
    index: i
  }));
});

defineExpose({ score, scorePercent });

const unlockScreens = inject('unlockScreens');

const getSectionAudioConfig = (
  section: HTMLElement
): {
  audioKey: string;
  slotName?: string;
  introIndex?: number;
  resultScreen?: NarratableScreen;
} | null => {
  const id = props.screen.id;
  if (section.classList.contains('intro')) {
    return {
      audioKey: hsgIntroMarkerKey(id),
      introIndex: Number(section.dataset.introIndex ?? 0)
    };
  }

  const levelClass = [...section.classList].find(c => c.startsWith('level_'));
  if (levelClass) {
    const i = Number(levelClass.replace('level_', ''));
    return {
      audioKey: hsgLevelMarkerKey(id, i),
      slotName: `level-${i}`
    };
  }

  if (section.classList.contains('result')) {
    const resultScreen = activeResult.value[0] as NarratableScreen | undefined;
    return {
      audioKey: hsgResultMarkerKey(id),
      resultScreen
    };
  }

  return null;
};

const resolveSectionAudio = async (
  section: HTMLElement
): Promise<string | null> => {
  const config = getSectionAudioConfig(section);
  if (!config) return null;

  if (audioAiJitEnabled.value) {
    if (config.introIndex !== undefined) {
      const row = props.screen.intro[config.introIndex] as
        | Record<string, unknown>
        | undefined;
      if (!row) return null;
      const text = buildHsgSectionRowText(row, resolveText, {
        introKeyPath: { hostId: props.screen.id, index: config.introIndex }
      });
      if (!text.trim()) return null;
      return resolveSlot('intro-section', text, config.audioKey);
    }

    if (config.resultScreen) {
      const text = buildHsgResultText(
        config.resultScreen as unknown as Record<string, unknown>,
        props.screen.id,
        scorePercent.value,
        resolveText
      );
      if (!text.trim()) return null;
      return resolveSlot('intro', text, config.audioKey);
    }

    if (!config.slotName) return null;
    return resolveSlot(config.slotName, undefined, config.audioKey);
  }

  const queued = section.querySelector<HTMLElement>('[data-audio]');
  const audio = section.dataset.audio ?? queued?.dataset.audio;
  return audio && audio !== '' ? audio : null;
};

const playSectionAudio = async (section: HTMLElement) => {
  const gen = ++sectionPlayGeneration;
  stop();

  const url = await resolveSectionAudio(section);
  if (gen !== sectionPlayGeneration || !url) return;

  play(url, { delay: 0.5, owner: props.screen.id });
};

const setupScrollTriggers = sectionClass => {
  const el = unref(elRef);
  if (!el) return;

  // we set up the scroll triggers separately for each hsg-secton using class identifiers (intro, level_0, level_1, result) as they unlock and redraw with some height
  const sections = el.querySelectorAll(`.hsg-section.${sectionClass}`);
  sections.forEach((section: HTMLElement) => {
    const scroller = section.closest('.scroller');

    let isTall = section.offsetHeight > window.innerHeight * 0.6;

    ScrollTrigger.create({
      trigger: section,
      scroller,
      start: () => (isTall ? 'top 50%' : '90% bottom'),
      end: () => (isTall ? 'bottom 50%' : '105% bottom'),
      onToggle: toggleParams => {
        const { isActive } = toggleParams;

        if (isActive && section.offsetHeight > 0) {
          playSectionAudio(section);
        }
      }
    });
  });
};

const reset = async () => {
  stop();
  // scroll to the start of the HSG
  await scrollToId(`${props.screen.id}_intro`);

  // pause before resetting suspend to prevent auto completion
  await new Promise(r => setTimeout(r, 500));

  // loop through all levels and the screen they contain and reset the suspend data and question correctQuestion and questionOptions
  set(`completed.${props.screen.id}`, false);
  set(`completed.${props.screen.id}_begin`, false);

  props.screen.levels.forEach(level => {
    if (level.id) set(`completed.${level.id}`, false);

    Object.values(level.targets).forEach(([screen], i) => {
      const { template, id } = screen;
      set(`completed.${id}`, false);

      // TODO - have a universal way to reset suspend data for all templates, or pass responsibility for this to hotspot
      const lightboxId = getLightboxId(level.id, Object.keys(level.targets)[i]);
      const hotspotSuspendKey = `hotspot ${level.id} ${
        Object.keys(level.targets)[i]
      }`;
      set(`completed.${hotspotSuspendKey}`, false); // the hotspot suspend completion
      set(`completed.${lightboxId}`, false); // the hotspot lighbox completion
      set(`completed.lightboxcontent_${lightboxId}`, false); // the hotspot lighbox content completion

      if (template && scoredTemplates.includes(template)) {
        set(`correctQuestions.${id}`, false);
        if (suspend.questionOptions[id]) {
          set(`questionOptions.${id}`, false);
        }
      }
    });
  });
};

onMounted(() => {
  setupScrollTriggers('intro');
});
</script>

<style>
#app .screen--hotspot-game {
  .screen-grid {
    margin: 0;
  }

  .display-yaml-wrapper {
    display: block;
    button {
      color: var(--color-gray-900) !important;
      background-color: white !important;
    }
  }

  .uc-hotspot {
    width: 100%;
    height: 100vh;

    /* flip the image for RTL layout - could cause mirroring issues (text, icons, numbers etc.) */
    @apply rtl_-scale-x-100;

    svg {
      width: 100px;
      height: auto;
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  /* hide the clickable svg targets on mobile layout */
  svg [data-class='targets'] {
    display: none !important;

    @media screen(lg) {
      display: block !important;
    }
  }
}
</style>
